import {
  BookingPreference,
  BookingPreferenceOption,
} from '../../../../utils/bookingPreferences/bookingPreferences';
import { CalendarState, TFunction } from '../../controller';
import {
  DisplayTarget,
  PreferenceToDisplayMapping,
  PreferenceType,
} from '@wix/bookings-services-preferences-modal/utils';
import Experiments from '@wix/wix-experiments';
import { Preference } from '../../../../types/types';
import { canAutoAssignStaff } from '../../../../utils/serviceUtils/serviceUtils';
import { DO_NOT_CARE_STAFF } from '../../../../constants/constants';
import { isLightBox } from '@wix/bookings-catalog-calendar-viewer-utils';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { BookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/types';
import { isBookFlowSettingsEnabled as isBookFlowSettingsEnabledUtil } from '@wix/bookings-calendar-catalog-viewer-mapper';

export type BookingDetailsPreferences = {
  bookingPreferences: BookingPreference[];
  titleText: string;
  clearText: string;
  appendToViewPort: boolean;
};

export const createBookingDetailsPreferences = ({
  state,
  context,
  preferences,
}: {
  state: CalendarState;
  context: CalendarContext;
  preferences: BookingPreference[];
}): BookingDetailsPreferences | undefined => {
  const {
    getContent,
    settingsParams,
    t,
    settings,
    flowAPI,
    experiments,
    isDateAndTimeViewMode,
    bookingsSettings,
  } = context;

  if (isDateAndTimeViewMode) {
    return undefined;
  }

  const { selectableSlotsAtSelectedTime = [], appointmentPreferencesDisplay } =
    state;

  const filteredBookingPreferences = filterBookingPreferences({
    preferences,
    experiments,
    appointmentPreferencesDisplay,
    bookingsSettings,
  });

  sortBookingPreferencesOptions(filteredBookingPreferences);

  const staffMemberPreferences = filteredBookingPreferences.find(
    (preference) => preference.key === Preference.STAFF_MEMBER,
  );
  const isAnyStaffMemberSubTitleEnabled = experiments.enabled(
    'specs.bookings.any-staff-member.subtitle',
  );

  if (
    staffMemberPreferences &&
    canAutoAssignStaff({
      state,
      context,
      selectableSlotsAtSelectedTime,
    })
  ) {
    staffMemberPreferences?.options.unshift({
      id: DO_NOT_CARE_STAFF,
      value: t('app.preferences.any-available-staff'),
      isSelectable: true,
      subtitle: isAnyStaffMemberSubTitleEnabled
        ? t('app.preferences.any-available-staff-subtitle')
        : undefined,
    });
  }
  const bookingPreferences = getBookingPreferencesOptionsWithEnrichedValue({
    bookingPreferences: filteredBookingPreferences,
    waitlistIndicationText: getContent({
      settingsParam: settingsParams.waitlistIndication,
      translationKey: 'app.settings.defaults.waitlist',
    }),
    t,
  });

  return {
    bookingPreferences,
    clearText: settings.get(settingsParams.bookingDetailsClearText),
    titleText: getContent({
      settingsParam: settingsParams.preferencesTitle,
      translationKey: 'app.settings.defaults.booking-details.preferences.title',
    }),
    appendToViewPort: !isLightBox(flowAPI.controllerConfig.wixCodeApi),
  };
};

const getBookingPreferencesOptionsWithEnrichedValue = ({
  bookingPreferences,
  waitlistIndicationText,
  t,
}: {
  bookingPreferences: BookingPreference[];
  waitlistIndicationText: string;
  t: TFunction;
}): BookingPreference[] => {
  return bookingPreferences.map((bookingPreference: BookingPreference) => {
    let options: BookingPreferenceOption[];
    if (bookingPreference.options.length > 1) {
      options = bookingPreference.options.map(
        (bookingPreferenceOption: BookingPreferenceOption) => {
          const valueWithWaitlistOnlyIndication = t(
            'app.booking-details.dropdowns.option-with-waitlist-only',
            {
              option: bookingPreferenceOption.value,
              waitlistOnly: waitlistIndicationText,
            },
          );
          return {
            ...bookingPreferenceOption,
            value: bookingPreferenceOption.isWithWaitingList
              ? valueWithWaitlistOnlyIndication
              : bookingPreferenceOption.value,
          };
        },
      );
    } else {
      options = bookingPreference.options;
    }

    return {
      ...bookingPreference,
      options,
    };
  });
};

const sortBookingPreferencesOptions = (
  bookingPreferences: BookingPreference[],
) => {
  bookingPreferences.forEach((preference) =>
    preference.options.sort((firstOption, secondOption) =>
      firstOption.value!.localeCompare(secondOption.value!),
    ),
  );
};

const filterBookingPreferences = ({
  preferences,
  experiments,
  appointmentPreferencesDisplay,
  bookingsSettings,
}: {
  preferences: BookingPreference[];
  experiments: Experiments;
  appointmentPreferencesDisplay?: PreferenceToDisplayMapping;
  bookingsSettings?: BookingsSettings;
}): BookingPreference[] => {
  const isBookFlowSettingsEnabled = isBookFlowSettingsEnabledUtil({
    experiments,
    bookingsSettings,
  });
  if (!isBookFlowSettingsEnabled || !appointmentPreferencesDisplay) {
    return preferences;
  }

  return preferences.filter((preference) => {
    switch (preference.key) {
      case Preference.STAFF_MEMBER:
        return (
          appointmentPreferencesDisplay[PreferenceType.STAFF_MEMBER] ===
          DisplayTarget.CALENDAR
        );
      case Preference.LOCATION:
        return (
          appointmentPreferencesDisplay[PreferenceType.LOCATION] ===
          DisplayTarget.CALENDAR
        );
      case Preference.DURATION:
      case Preference.CUSTOM:
        return (
          appointmentPreferencesDisplay[PreferenceType.DYNAMIC_PRICE] ===
          DisplayTarget.CALENDAR
        );
      default:
        return true;
    }
  });
};
